import { useState, useEffect } from 'react';
import { 
  Shield, Users, Layers, Briefcase, BarChart3, Plus, Trash2, 
  Zap, Ban, DollarSign, Edit, Check, X, Copy, AlertTriangle,
  Wallet, Gift, Fuel, ToggleLeft, ToggleRight, Crown,
  RefreshCw, UserCheck, UserX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'gaurilabs_access_token';

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// 3-6-9 Tier Definitions
const LICENSE_TIERS = [
  { id: 'STRATEGY', number: '3', name: 'Strategy', label: "The '3'", tagline: 'Begin the calibration', price: 49, credits: 50, bot_limit: 2, duration: '30d', features: { volume_bot: true, lure_bot: false }, color: '#06b6d4', ring: 'ring-[#06b6d4]', badge: 'bg-[#06b6d4]/10 text-[#06b6d4]', priceColor: 'text-[#06b6d4]' },
  { id: 'HARMONY',  number: '6', name: 'Harmony',  label: "The '6'", tagline: 'Synchronise the flow',  price: 149, credits: 250, bot_limit: 6, duration: '30d', features: { volume_bot: true, lure_bot: true  }, color: '#f97316', ring: 'ring-[#f97316]', badge: 'bg-[#f97316]/10 text-[#f97316]', priceColor: 'text-[#f97316]' },
  { id: 'POWER',    number: '9', name: 'Power',    label: "The '9'", tagline: 'Command the grid',      price: 499, credits: 1000, bot_limit: 10, duration: '30d', features: { volume_bot: true, lure_bot: true  }, color: '#eab308', ring: 'ring-[#eab308]', badge: 'bg-[#eab308]/10 text-[#eab308]', priceColor: 'text-[#eab308]' },
];

const NETWORKS = ['Solana', 'Ethereum', 'USDC (TRC-20)', 'USDC (ERC-20)'];

const MOCK_AGENCIES = [
  { id: 'AGY-0042', name: 'Nexus Digital', link: 'https://gaurilabs.com/ref/nexus-0042', sla_target: 150, status: 'active', sales_this_week: 87, escrow: 12450 },
  { id: 'AGY-0078', name: 'Apex Trading Co', link: 'https://gaurilabs.com/ref/apex-0078', sla_target: 200, status: 'active', sales_this_week: 195, escrow: 8900 },
];

const TABS = [
  { id: 'agencies', label: 'Agency Onboarding', icon: Users },
  { id: 'tiers',    label: 'Tier & Subscriptions', icon: Layers },
  { id: 'clients',  label: 'Clients (B2B)', icon: Briefcase },
];

export default function AdminGodMode() {
  const [activeTab, setActiveTab] = useState('tiers');
  const [agencies, setAgencies] = useState(MOCK_AGENCIES);
  const [showAddAgency, setShowAddAgency] = useState(false);
  const [newAgency, setNewAgency] = useState({ name: '', sla_target: 100, email: '', password: '' });

  // Real sub-users from API
  const [subUsers, setSubUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Create Sub-User modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTier, setSelectedTier] = useState('HARMONY');
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', payment_tx_hash: '', payment_network: 'Solana' });

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) setSubUsers(data.users || []);
      else toast.error(data.detail || 'Failed to load users');
    } catch {
      toast.error('Failed to connect to server');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Account ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'}`);
        setSubUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      } else {
        toast.error(data.detail || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to connect to server');
    }
  };

  const handleApplyTier = async (userId, tier) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/tier`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ tier }),
      });
      if (res.ok) {
        toast.success(`Tier updated to ${tier}`);
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update tier');
      }
    } catch {
      toast.error('Failed to connect to server');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email and password are required.');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          tier: selectedTier,
          payment_tx_hash: newUser.payment_tx_hash || '',
          payment_network: newUser.payment_network,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      toast.success(`Account created for ${newUser.email} — ${selectedTier} tier`);
      setShowCreateUser(false);
      setNewUser({ name: '', email: '', password: '', payment_tx_hash: '', payment_network: 'Solana' });
      setSelectedTier('HARMONY');
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleForceEscrow = (id) => toast.success(`Escrow payout force-executed for ${id}`);
  const handlePenalty    = (id) => toast.success(`10% SLA Liquid Penalty triggered for ${id}`);
  const handleRevoke     = (id) => { setAgencies(a => a.map(x => x.id === id ? {...x, status: 'revoked'} : x)); toast.success(`Agency ${id} revoked`); };

  const tierInfo = LICENSE_TIERS.find(t => t.id === selectedTier);

  return (
    <div className="space-y-6" data-testid="admin-godmode">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#f97316] drop-shadow-[0_0_8px_#f97316]" />
            GOD-MODE CONTROL
          </h1>
          <p className="text-sm font-mono text-[#64748b] mt-1">Absolute CRUD | All systems override</p>
        </div>
        <Button onClick={() => setShowCreateUser(true)}
          className="bg-[#f97316] hover:bg-[#ea580c] text-white font-mono text-xs flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create Sub-User
        </Button>
      </div>

      {/* Create Sub-User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0B0F19] border border-[#1e293b] rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[#1e293b]">
              <div>
                <h2 className="text-white font-mono font-bold text-base flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#f97316]" /> Create Sub-User
                </h2>
                <p className="text-[#64748b] font-mono text-xs mt-0.5">Provision a new client account</p>
              </div>
              <button onClick={() => setShowCreateUser(false)} className="text-[#64748b] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#64748b] font-mono text-xs mb-1 block">Name</Label>
                  <Input placeholder="Client name" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})}
                    className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-xs h-9" />
                </div>
                <div>
                  <Label className="text-[#64748b] font-mono text-xs mb-1 block">Email</Label>
                  <Input placeholder="client@example.com" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                    className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-xs h-9" />
                </div>
              </div>
              <div>
                <Label className="text-[#64748b] font-mono text-xs mb-1 block">Password</Label>
                <Input type="password" placeholder="Set account password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-xs h-9" />
              </div>
              <div>
                <Label className="text-[#64748b] font-mono text-xs mb-2 block">License Tier</Label>
                <div className="grid grid-cols-3 gap-2">
                  {LICENSE_TIERS.map(tier => (
                    <button key={tier.id} onClick={() => setSelectedTier(tier.id)}
                      className={`rounded-xl border p-3 text-left transition-all ${selectedTier === tier.id ? `${tier.ring} ring-2 border-transparent bg-[#1e293b]` : 'border-[#1e293b] hover:border-[#334155] bg-[#0d1117]'}`}>
                      <div className={`text-xs font-mono mb-0.5 ${tier.badge} inline-block px-1.5 py-0.5 rounded`}>{tier.label}</div>
                      <div className="text-white font-bold font-mono text-sm mt-1">{tier.name}</div>
                      <div className={`font-mono font-bold text-lg ${tier.priceColor}`}>${tier.price}<span className="text-[10px] text-[#64748b] font-normal">/mo</span></div>
                      <div className="text-[#64748b] font-mono text-[10px] mt-1">{tier.bot_limit} bots / {tier.duration}</div>
                      <div className="mt-1.5 space-y-0.5">
                        <div className="flex items-center gap-1 text-[10px] font-mono text-[#94a3b8]"><Check className="w-3 h-3 text-[#06b6d4]" /> Volume Bot</div>
                        <div className={`flex items-center gap-1 text-[10px] font-mono ${tier.features.lure_bot ? 'text-[#94a3b8]' : 'text-[#475569]'}`}>
                          {tier.features.lure_bot ? <Check className="w-3 h-3 text-[#06b6d4]" /> : <X className="w-3 h-3 text-[#475569]" />} Lure Bot
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {tierInfo && <p className="text-[10px] font-mono text-[#64748b] mt-2">Selected: <span className={tierInfo.priceColor}>{tierInfo.name}</span> — {tierInfo.credits} credits · {tierInfo.bot_limit} bots · ${tierInfo.price}/mo</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#64748b] font-mono text-xs mb-1 block">Payment TX Hash</Label>
                  <Input placeholder="Optional" value={newUser.payment_tx_hash} onChange={e => setNewUser({...newUser, payment_tx_hash: e.target.value})}
                    className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-xs h-9" />
                </div>
                <div>
                  <Label className="text-[#64748b] font-mono text-xs mb-1 block">Payment Network</Label>
                  <select value={newUser.payment_network} onChange={e => setNewUser({...newUser, payment_network: e.target.value})}
                    className="w-full h-9 px-2 rounded-md bg-[#0B0F19] border border-[#1e293b] text-white font-mono text-xs">
                    {NETWORKS.map(n => <option key={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 pb-5 pt-2 border-t border-[#1e293b]">
              <Button onClick={handleCreateUser} disabled={creating} className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-mono text-sm h-10">
                {creating ? 'Creating...' : '+ Create Account & Assign License'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[#1e293b]/30 border border-[#1e293b]">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-mono font-medium transition-all ${activeTab === tab.id ? 'bg-[#f97316]/10 text-[#f97316]' : 'text-[#64748b] hover:text-white hover:bg-[#1e293b]/50'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Tier & Subscriptions Tab — REAL data */}
      {activeTab === 'tiers' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-mono text-[#64748b]">{subUsers.length} sub-users registered</p>
            <Button onClick={fetchUsers} variant="outline" size="sm" className="border-[#1e293b] text-[#64748b] font-mono text-xs bg-transparent hover:bg-[#1e293b]/50">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${usersLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          {/* Tier overview cards */}
          <div className="grid grid-cols-3 gap-4">
            {LICENSE_TIERS.map(tier => (
              <div key={tier.id} className="rounded-xl border border-[#1e293b] bg-[#0d1117] p-4">
                <div className={`text-[10px] font-mono mb-1 inline-block px-2 py-0.5 rounded ${tier.badge}`}>{tier.label}</div>
                <div className="text-white font-bold font-mono text-lg mt-1">{tier.name}</div>
                <div className={`font-mono font-bold text-2xl ${tier.priceColor}`}>${tier.price}<span className="text-xs text-[#64748b] font-normal">/mo</span></div>
                <div className="mt-2 space-y-1 text-[11px] font-mono text-[#94a3b8]">
                  <div>{tier.bot_limit} bots · {tier.credits} credits</div>
                  <div className="flex items-center gap-1"><Check className="w-3 h-3 text-[#06b6d4]" /> Volume Bot</div>
                  <div className={`flex items-center gap-1 ${tier.features.lure_bot ? '' : 'text-[#475569]'}`}>
                    {tier.features.lure_bot ? <Check className="w-3 h-3 text-[#06b6d4]" /> : <X className="w-3 h-3 text-[#475569]" />} Lure Bot
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Real users table */}
          <div className="rounded-xl border border-[#1e293b] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#1e293b]/50">
                <tr className="text-[10px] font-mono text-[#64748b] uppercase tracking-widest">
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-center">Tier</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Bots</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e293b]">
                {usersLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-[#64748b] text-xs">Loading users...</td></tr>
                ) : subUsers.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-[#64748b] text-xs">No sub-users yet. Click "Create Sub-User" to add one.</td></tr>
                ) : subUsers.map(u => {
                  const tier = LICENSE_TIERS.find(t => t.id === u.license?.tier) || LICENSE_TIERS[0];
                  const isActive = u.status === 'ACTIVE';
                  return (
                    <tr key={u.id} className="hover:bg-[#1e293b]/20 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-mono text-white text-xs">{u.name || u.email}</p>
                        <p className="text-[10px] font-mono text-[#64748b]">{u.email}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <select defaultValue={u.license?.tier || 'STRATEGY'}
                          onChange={e => handleApplyTier(u.id, e.target.value)}
                          className="h-7 px-2 rounded bg-[#0B0F19] border border-[#1e293b] text-xs font-mono text-white">
                          {LICENSE_TIERS.map(t => <option key={t.id} value={t.id}>{t.name} — ${t.price}/mo</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${isActive ? 'bg-[#06b6d4]/10 text-[#06b6d4]' : 'bg-[#ef4444]/10 text-[#ef4444]'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-[#64748b] text-xs">
                        {u.running_bots}/{u.license?.bot_limit || 0} running
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleToggleStatus(u.id, u.status)}
                          title={isActive ? 'Suspend account' : 'Activate account'}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-mono font-bold ml-auto transition-colors ${
                            isActive
                              ? 'bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20'
                              : 'bg-[#06b6d4]/10 text-[#06b6d4] hover:bg-[#06b6d4]/20'
                          }`}>
                          {isActive
                            ? <><UserX className="w-3 h-3" /> Suspend</>
                            : <><UserCheck className="w-3 h-3" /> Activate</>
                          }
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agency Tab */}
      {activeTab === 'agencies' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-mono text-[#64748b]">{agencies.length} agencies registered</p>
            <Button onClick={() => setShowAddAgency(!showAddAgency)} className="bg-[#f97316] hover:bg-[#ea580c] text-white font-mono text-xs">
              <Plus className="w-4 h-4 mr-1" /> Add Agency
            </Button>
          </div>
          {showAddAgency && (
            <div className="p-5 rounded-xl border border-[#f97316]/30 bg-[#f97316]/5">
              <p className="text-sm font-mono font-bold text-[#f97316] mb-4">NEW AGENCY</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-mono text-[#64748b]">Agency Name</Label>
                  <Input value={newAgency.name} onChange={e => setNewAgency(p => ({...p, name: e.target.value}))} className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-sm" /></div>
                <div><Label className="text-xs font-mono text-[#64748b]">SLA Target</Label>
                  <Input type="number" value={newAgency.sla_target} onChange={e => setNewAgency(p => ({...p, sla_target: e.target.value}))} className="bg-[#0B0F19] border-[#1e293b] text-white font-mono text-sm" /></div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button onClick={() => { toast.success('Agency created'); setShowAddAgency(false); }} className="bg-[#06b6d4] hover:bg-[#0891b2] text-white font-mono text-xs">
                  <Check className="w-3.5 h-3.5 mr-1" /> Create
                </Button>
                <Button onClick={() => setShowAddAgency(false)} variant="outline" className="border-[#1e293b] text-[#64748b] font-mono text-xs bg-transparent">
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-[#1e293b] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#1e293b]/50">
                <tr className="text-[10px] font-mono text-[#64748b] uppercase tracking-widest">
                  <th className="px-4 py-3 text-left">Agency</th>
                  <th className="px-4 py-3 text-left">Link</th>
                  <th className="px-4 py-3 text-center">SLA</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e293b]">
                {agencies.map(a => (
                  <tr key={a.id} className="hover:bg-[#1e293b]/20">
                    <td className="px-4 py-3"><p className="font-mono font-medium text-white">{a.name}</p><p className="text-[10px] font-mono text-[#64748b]">{a.id}</p></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-[#06b6d4] truncate max-w-[180px]">{a.link}</span>
                        <button onClick={() => { navigator.clipboard.writeText(a.link); toast.success('Copied!'); }} className="text-[#64748b] hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-white">{a.sla_target}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${a.status === 'active' ? 'bg-[#06b6d4]/10 text-[#06b6d4]' : 'bg-[#ef4444]/10 text-[#ef4444]'}`}>{a.status.toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleForceEscrow(a.id)} className="p-1.5 rounded hover:bg-[#06b6d4]/10 text-[#64748b] hover:text-[#06b6d4]"><DollarSign className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handlePenalty(a.id)} className="p-1.5 rounded hover:bg-[#eab308]/10 text-[#64748b] hover:text-[#eab308]"><AlertTriangle className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleRevoke(a.id)} className="p-1.5 rounded hover:bg-[#ef4444]/10 text-[#64748b] hover:text-[#ef4444]"><Ban className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="rounded-xl border border-[#1e293b] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#1e293b]/50">
              <tr className="text-[10px] font-mono text-[#64748b] uppercase tracking-widest">
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-center">Tier</th>
                <th className="px-4 py-3 text-center">Bots</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e293b]">
              {subUsers.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center font-mono text-[#64748b] text-xs">No clients yet.</td></tr>
              ) : subUsers.map(u => {
                const isActive = u.status === 'ACTIVE';
                return (
                  <tr key={u.id} className="hover:bg-[#1e293b]/20">
                    <td className="px-4 py-3"><p className="font-mono text-white text-xs">{u.name}</p><p className="text-[10px] font-mono text-[#64748b]">{u.email}</p></td>
                    <td className="px-4 py-3 text-center font-mono text-[#06b6d4] text-xs">{u.license?.tier || 'STRATEGY'}</td>
                    <td className="px-4 py-3 text-center font-mono text-[#64748b] text-xs">{u.running_bots} running</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${isActive ? 'bg-[#06b6d4]/10 text-[#06b6d4]' : 'bg-[#ef4444]/10 text-[#ef4444]'}`}>{u.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleToggleStatus(u.id, u.status)}
                        className={`px-3 py-1.5 rounded text-[10px] font-mono font-bold ${isActive ? 'bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20' : 'bg-[#06b6d4]/10 text-[#06b6d4] hover:bg-[#06b6d4]/20'}`}>
                        {isActive ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
